import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Loader from "../child/Loder";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { fetchCompatibilityById } from "../services/compatibilityService";
import { fetchVaultUnlockDetails } from "../services/matchService";
import {
  normalizeCompatibilityRecord,
  isLikelyObjectId,
  extractRecordFromInteractionResponse,
  pausedCloseLabel,
  pauseCountedAsEndLine,
} from "./compatibilityRecordUtils";
import moment from "moment";
import ChatMessagesTable from "../ChatList/ChatList";
import VaultLinesPanel from "./VaultLinesPanel";

// Honest status labels (founder 2026-08-31). "expired" is any pre-reveal
// ending: a member's End (endedBy set) reads "Ended before reveal", a
// machine close (endedBy empty) reads "Timed out before reveal" (founder
// 2026-09-24). "unmatched" is the post-reveal close. Display-only, the DB
// enum is untouched.
const STATUS_LABELS = {
  generated: "Active",
  matched: "Matched",
  expired: "Ended before reveal",
  unmatched: "Closed after reveal",
  blocked: "Blocked",
  qa_parked: "QA parked",
};

// End Compatibility reason chips (v2.0 confirm sheet; server enum in
// endCompatibilitySession). Older clients send none.
const END_REASON_LABELS = {
  not_feeling_it: "Not feeling it",
  not_the_right_time: "Not the right time",
  too_far_away: "Too far away",
  curious_whos_next: "Curious who's next",
  prefer_not_to_say: "Prefer not to say",
  // Machine reasons that land in endReason (no person tapped End), in words
  // (founder 2026-09-24).
  daily_expired: "Nobody opened it in a day",
  quiet_auto: "Went quiet, no reveal",
  quiet_mutual: "Both went quiet",
  quiet_stalled: "Conversation went quiet",
  partner_left: "The other member left",
  // One of them paused their introductions (2026-09-27); the status line
  // names who ("Closed: {First} took a break").
  paused: "Took a break",
};

const ViewCompatibilityPageNew = () => {
  const { id } = useParams();

  // The Introductions board links straight to a tab (2026-10-20):
  // ?tab=chat (Chat transcript) or ?tab=vault-unlock (Vault and reveal).
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(location.search).get("tab");
    return ["compatibility-status", "vault-unlock", "chat"].includes(t) ? t : "compatibility-status";
  });
  const [row, setRow] = useState(null);
  const [normalized, setNormalized] = useState(null);
  const [vaultData, setVaultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setVaultData(null);
      try {
        const res = await fetchCompatibilityById(id);
        const body = res?.data;
        const record = extractRecordFromInteractionResponse(res);
        if (record?._id) {
          setRow(record);
          const norm = normalizeCompatibilityRecord(record);
          setNormalized(norm);

          const { fromUserId: fromId, toUserId: toId } = norm;
          if (isLikelyObjectId(fromId) && isLikelyObjectId(toId)) {
            const vaultRes = await fetchVaultUnlockDetails(fromId, toId);
            if (vaultRes?.data?.status === 1) {
              setVaultData(vaultRes.data.data);
            }
          }
        } else {
          setErrorMessage(body?.message || "Failed to fetch compatibility");
        }
      } catch (err) {
        setErrorMessage(err?.response?.data?.message || err?.message || "Server error");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  const session = vaultData?.revealTogetherSession;
  const isRevealTogetherUnlocked =
    session &&
    ((session.userAConfirmed && session.userBConfirmed) ||
      (session.initiatedBy && session.initiatedBy === "adminId"));
  const initiatedById = session && session?.initiatedBy?.toString?.();
  const userId = vaultData?.user?.id;
  const matchedUserId = vaultData?.matchedUser?.id;

  const d = row;
  const n = normalized;

  if (loading) return <Loader />;

  const chatReady =
    n && isLikelyObjectId(n.fromUserId) && isLikelyObjectId(n.toUserId);

  return (
    <div className="row gy-4">
      <div className="col-lg-12">
        {successMessage && (
          <SuccessAlert message={successMessage} onClose={() => setSuccessMessage("")} />
        )}
        {errorMessage && (
          <DengerAlert message={errorMessage} onClose={() => setErrorMessage("")} />
        )}
      </div>

      <div className="col-md-12">
        <div className="card h-100">
          <div className="card-body p-24">
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="compatibility-view-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "compatibility-status" ? "active" : ""
                  }`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("compatibility-status")}
                >
                  Compatibility Status
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "vault-unlock" ? "active" : ""
                  }`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("vault-unlock")}
                >
                  Vault Unlock
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className={`nav-link d-flex align-items-center px-24 ${
                    activeTab === "chat" ? "active" : ""
                  }`}
                  type="button"
                  role="tab"
                  onClick={() => setActiveTab("chat")}
                >
                  Chat
                </button>
              </li>
            </ul>

            <div className="tab-content" id="compatibility-view-tab-content">
              {activeTab === "compatibility-status" && d && (
                <div className="tab-pane fade show active" role="tabpanel">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">From User:</label>
                      <div>{n?.fromEmail || "N/A"}</div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">To User:</label>
                      <div>{n?.toEmail || "N/A"}</div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Compatibility Status:</label>
                      <div>
                        {/* A pause close has no ender either, but nothing
                            timed out: one of them asked for a break. */}
                        {d.compatibilityStatus
                          ? pausedCloseLabel(d) ||
                            (d.compatibilityStatus === "expired" && !d.endedBy
                              ? "Timed out before reveal"
                              : STATUS_LABELS[d.compatibilityStatus] ||
                                d.compatibilityStatus.charAt(0).toUpperCase() +
                                  d.compatibilityStatus.slice(1))
                          : "N/A"}
                      </div>
                      {/* A pause close that a quick resume counted as the
                          pauser's End (pauseCountedAsEndAt, 2026-09-27). */}
                      {pauseCountedAsEndLine(d) && (
                        <div className="text-sm text-secondary-light">{pauseCountedAsEndLine(d)}</div>
                      )}
                      {d.compatibilityStatus === "expired" && d.endedBy && (
                        <div className="text-sm text-secondary-light">
                          by{" "}
                          {String(d.endedBy?._id || d.endedBy) === String(d.userA?._id)
                            ? d.userA?.email
                            : String(d.endedBy?._id || d.endedBy) === String(d.userB?._id)
                              ? d.userB?.email
                              : "unknown"}
                        </div>
                      )}
                      {d.endReason && !pausedCloseLabel(d) && (
                        <div className="mt-1">
                          <span className="badge bg-neutral-200 text-neutral-600">
                            {END_REASON_LABELS[d.endReason] || d.endReason}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Common Avg (match score):</label>
                      <div>{d.commonAvg != null ? `${d.commonAvg}%` : "N/A"}</div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Current To Match:</label>
                      <div>{d.currentToMatch != null ? `${d.currentToMatch}%` : "N/A"}</div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Match To Current:</label>
                      <div>{d.matchToCurrent != null ? `${d.matchToCurrent}%` : "N/A"}</div>
                    </div>
                  
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Last Calculated At:</label>
                      <div>
                        {d.lastCalculatedAt
                          ? moment(d.lastCalculatedAt).format("DD-MM-YYYY HH:mm")
                          : "N/A"}
                      </div>
                    </div>
                 
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Created At:</label>
                      <div>
                        {d.createdAt ? moment(d.createdAt).format("DD-MM-YYYY HH:mm") : "N/A"}
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-bold">Updated At:</label>
                      <div>
                        {d.updatedAt ? moment(d.updatedAt).format("DD-MM-YYYY HH:mm") : "N/A"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "vault-unlock" && (
                <div className="tab-pane fade show active" role="tabpanel">
                  {!vaultData ? (
                    <p className="text-muted mb-0">
                      {chatReady
                        ? "No vault unlock details available for this pair."
                        : "Vault details require valid user identifiers for both users."}
                    </p>
                  ) : (
                    <>
                      <div className="row mb-4">
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold">User:</label>
                          <div>
                            {vaultData.user?.fullName} ({vaultData.user?.email})
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <label className="form-label fw-bold">Matched With:</label>
                          <div>
                            {vaultData.matchedUser?.fullName} ({vaultData.matchedUser?.email})
                          </div>
                        </div>
                        <div className="col-md-4">
                          <label className="form-label fw-bold">Reveal Together Unlocked:</label>
                          <div
                            className={isRevealTogetherUnlocked ? "text-success" : "text-danger"}
                          >
                            {isRevealTogetherUnlocked ? "Yes" : "No"}
                          </div>
                        </div>
                      </div>

                      <label className="form-label fw-bold mb-2">Tier Progress (Both Users)</label>
                      <div className="card mb-4 shadow-sm">
                        <div className="card-body p-0">
                          <div className="table-responsive">
                            <table className="table table-bordered align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Tier</th>
                                  <th>{vaultData.user?.fullName} Progress</th>
                                  <th>{vaultData.matchedUser?.fullName} Progress</th>
                                  <th>Both Completed</th>
                                  <th>Admin Unlock</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vaultData.tierDetails?.map((tier, index) => (
                                  <tr key={index}>
                                    <td>
                                      <strong>{tier.tier}</strong>
                                      <br />
                                      <small className="text-muted">{tier.subText}</small>
                                    </td>
                                    <td>
                                      {tier.userProgress?.currentStep}/{tier.totalSteps} (
                                      {Number(tier.userProgress?.progressPercent || 0).toFixed(0)}%)
                                    </td>
                                    <td>
                                      {tier.matchedUserProgress?.currentStep}/{tier.totalSteps} (
                                      {Number(
                                        tier.matchedUserProgress?.progressPercent || 0
                                      ).toFixed(0)}
                                      %)
                                    </td>
                                    <td>
                                      {tier.bothCompleted ? (
                                        <span className="badge bg-success">Yes</span>
                                      ) : (
                                        <span className="badge bg-secondary">No</span>
                                      )}
                                    </td>
                                    <td>
                                      {tier.unlockedByAdmin ? (
                                        <span className="badge bg-warning text-dark">Yes</span>
                                      ) : (
                                        "N/A"
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>

                      {/* Add a line (2026-10-08): the pair's typed Vault lines and refused lines. */}
                      {chatReady && (
                        <VaultLinesPanel
                          userId={n.fromUserId}
                          matchedUserId={n.toUserId}
                          names={{
                            [vaultData.user?.id]: vaultData.user?.fullName,
                            [vaultData.matchedUser?.id]: vaultData.matchedUser?.fullName,
                          }}
                        />
                      )}

                      {vaultData.revealTogetherSession && (
                        <>
                          <label className="form-label fw-bold mt-4">Reveal Together</label>
                          <div className="card shadow-sm mb-4">
                            <div className="card-body p-0">
                              <div className="table-responsive">
                                <table className="table table-bordered align-middle mb-0">
                                  <thead className="table-light">
                                    <tr>
                                      <th>User</th>
                                      <th>User</th>
                                      <th>Initiated By</th>
                                      <th>Start Time</th>
                                      <th>Expires At</th>
                                      <th>Status</th>
                                      <th>Reignite Count</th>
                                      <th>Closed Reason</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td>
                                        {vaultData.revealTogetherSession.userA?.toString() ===
                                        vaultData.user?.id
                                          ? vaultData.user?.fullName
                                          : vaultData.matchedUser?.fullName}
                                        <br />
                                        {vaultData.revealTogetherSession.userAConfirmed ? (
                                          <span className="badge bg-success">Yes</span>
                                        ) : (
                                          <span className="badge bg-secondary">No</span>
                                        )}
                                      </td>
                                      <td>
                                        {vaultData.revealTogetherSession.userB?.toString() ===
                                        vaultData.user?.id
                                          ? vaultData.user?.fullName
                                          : vaultData.matchedUser?.fullName}
                                        <br />
                                        {vaultData.revealTogetherSession.userBConfirmed ? (
                                          <span className="badge bg-success">Yes</span>
                                        ) : (
                                          <span className="badge bg-secondary">No</span>
                                        )}
                                      </td>
                                      <td>
                                        {initiatedById === userId
                                          ? vaultData.user?.fullName
                                          : initiatedById === matchedUserId
                                            ? vaultData.matchedUser?.fullName
                                            : "Admin"}
                                      </td>
                                      <td>
                                        {moment(vaultData.revealTogetherSession.startTime).format(
                                          "DD-MM-YYYY HH:mm"
                                        )}
                                      </td>
                                      <td>
                                        {moment(vaultData.revealTogetherSession.expiresAt).format(
                                          "DD-MM-YYYY HH:mm"
                                        )}
                                      </td>
                                      <td>{vaultData.revealTogetherSession.status}</td>
                                      <td>{vaultData.revealTogetherSession.reigniteCount}</td>
                                      <td>{vaultData.revealTogetherSession.closedReason || "N/A"}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </>
                      )}

                      <label className="form-label fw-bold">Admin Actions Log</label>
                      {(vaultData.adminGlobalActions?.length > 0 ||
                        vaultData.tierDetails?.some((t) => (t.adminActions || []).length > 0)) && (
                        <div className="card shadow-sm">
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-bordered align-middle mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>Admin</th>
                                    <th>Email</th>
                                    <th>Tier</th>
                                    <th>Type</th>
                                    <th>Reason</th>
                                    <th>Permanent</th>
                                    <th>Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {vaultData.tierDetails?.flatMap((tier) =>
                                    (tier.adminActions || []).map((log, idx) => (
                                      <tr key={`${tier.tier}-${idx}`}>
                                        <td>{log.adminName}</td>
                                        <td>{log.adminEmail}</td>
                                        <td>{tier.tier}</td>
                                        <td>Tier Unlock</td>
                                        <td>{log.reason}</td>
                                        <td>{log.permanent ? "Yes" : "No"}</td>
                                        <td>{moment(log.date).format("DD-MM-YYYY")}</td>
                                      </tr>
                                    ))
                                  )}
                                  {(vaultData.adminGlobalActions || []).map((log, idx) => (
                                    <tr key={`g-${idx}`}>
                                      <td>{log.adminName}</td>
                                      <td>{log.adminEmail}</td>
                                      <td>All Tiers</td>
                                      <td>{log.unlockType}</td>
                                      <td>{log.reason}</td>
                                      <td>N/A</td>
                                      <td>{moment(log.date).format("DD-MM-YYYY")}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                      {!(
                        vaultData.adminGlobalActions?.length > 0 ||
                        vaultData.tierDetails?.some((t) => (t.adminActions || []).length > 0)
                      ) && <p className="text-muted">No admin actions recorded.</p>}
                    </>
                  )}
                </div>
              )}

              {activeTab === "chat" && (
                <>
                  {chatReady ? (
                    <ChatMessagesTable
                      defaultUid={`cm_${n.fromUserId}`}
                      defaultOtherUser={`cm_${n.toUserId}`}
                    />
                  ) : (
                    <p className="text-muted mb-0">
                      Chat is unavailable until both users have valid IDs in this compatibility
                      record.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewCompatibilityPageNew;
